import React from 'react';
import { Component, Container, PuzzleCheckbox, PuzzleToggle } from "puzzle"
import _ from 'lodash';
import PreviewDeliverablesSubmissionSection from "./PreviewDeliverablesSubmissionSection";
import { CHECKBOX_JOB, JOB, SUBMISSION, SUBMISSION_POST_DATA } from './Types';

import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";

export type PROPS = {};

export type STATE = {
  activeTabLanguages: boolean,
  activeTab: string,
  checkboxes: CHECKBOX_JOB[],
  submissions: SUBMISSION[],
}

class PreviewDeliverables extends Component<PROPS, STATE> {
  public state: STATE = {
    activeTabLanguages: true,
    activeTab: 'language',
    checkboxes: [],
    submissions: [],
  };

  /**
   * Returns true if all jobs are checked, false otherwise
   */
  areAllChecked = () => {
    const { checkboxes } = this.state;
    return checkboxes.every(job => job.checked);
  };

  /**
   *  Creates json object for post request
   */
  getJsonForPost = (): SUBMISSION_POST_DATA[] => {
    const { checkboxes } = this.state;
    const jsonForPost: SUBMISSION_POST_DATA[] = [];
    checkboxes.forEach(this.groupJobsBySubmissionsForPost.bind(this, jsonForPost));
    return jsonForPost;
  };

  /**
   * Returns the number of currently selected files
   */
  getNumberOfSelectedFiles = () => this.state.checkboxes.reduce(this.numberOfFilesReducer, 0);


  /**
   *  Since checkboxes within state.checkbox array are already grouped in order by submissions
   *  we search for checked checkbox and put it's jobId to last submission object (if submissionId is the same),
   *  or make new submission object with new jobIds array
   */
  groupJobsBySubmissionsForPost = (jsonForPost: SUBMISSION_POST_DATA[], checkbox: CHECKBOX_JOB) => {
    if (checkbox.checked) {
      const { jobId, submissionId } = checkbox;
      const lastSubmissionAdded = _.last(jsonForPost);
      if (lastSubmissionAdded?.submissionId === submissionId) {
        lastSubmissionAdded.jobIds.push(jobId);
      } else {
        jsonForPost.push({
          submissionId,
          jobIds: [jobId]
        })
      }
    }
  };

  /**
   * Loads all submissions and all checkboxes to state on init, and after the state changes it updates the footer text
   */
  loadSubmissions = (submissions: SUBMISSION[]) => {
    const checkboxes: CHECKBOX_JOB[] = [];
    submissions.forEach((submission: SUBMISSION) => {
      this.mapSubmissionJobs(submission, checkboxes);
    });
    this.setState({ submissions, checkboxes },
      this.updateDialogFooter
    );
  };

  /**
   * Maps submission's jobs and pushes to checkbox array
   */
  mapSubmissionJobs = (submission: SUBMISSION, checkboxes: CHECKBOX_JOB[]) => {
    const { jobs, submissionId } = submission;
    jobs && jobs.forEach((job: JOB) => {
      checkboxes.push({
        jobIndex: checkboxes.length,
        jobId: job.jobId,
        batchName: job.batchName,
        targetLocale: job.targetLocale,
        submissionId,
        checked: false,
        fileCount: job.fileCount
      })
    });
  };


  /**
   * Calculates total files count within checked jobs
   */
  numberOfFilesReducer = (totalCount: number, checkboxJob: CHECKBOX_JOB) => {
    return checkboxJob.checked ? totalCount + checkboxJob.fileCount : totalCount;
  };

  /**
   * DownloadAll checkbox change handler. Sets all jobs checked/unchecked
   */
  onCheckDownloadAllHandler = (event: CheckboxChangeEvent) => {
    const { value } = event;
    const { checkboxes } = this.state;
    checkboxes.forEach(job => {
      job.checked = value;
    });
    this.setState({ checkboxes },
      this.updateDialogFooter
    );
  };

  /**
   * Change handler for a job and/or for a group of jobs
   */
  onCheckChange = (jobIndex: number[] | number, event: CheckboxChangeEvent) => {
    const { checkboxes } = this.state;
    const { value } = event;
    if (_.isArray(jobIndex)) {
      jobIndex.forEach((index: number) => {
        checkboxes[index].checked = value;
      });
    } else {
      checkboxes[jobIndex].checked = value;
    }
    this.setState({ checkboxes },
      this.updateDialogFooter
    );
  };

  /**
   * Set active tab state
   * @param tab
   */
  setActiveTab = (tab: string) => {
    this.setState({
      activeTabLanguages: tab === 'language',
      activeTab: tab
    });
  }

  /**
   * Renders submission section in dialog
   * @param submission
   * @param startIndex in state.checkboxes of jobs from current submission
   */
  renderSubmission = (submission: SUBMISSION, startIndex: number) => {
    const { activeTabLanguages } = this.state;
    const checkboxes = [...this.state.checkboxes].slice(startIndex, startIndex + submission.jobs.length);
    const props = {
      activeTabLanguages,
      checkboxes,
      startIndex,
      onCheckChange: this.onCheckChange
    };
    return (
      <div role="rowgroup" className={'tp-submission'} key={'submissionPreviewDeliverable-' + startIndex}>
        <PreviewDeliverablesSubmissionSection {...props} key={'submissionPreviewDeliverables-' + startIndex} />
      </div>
    )
  };

  /**
   * Updates dialog footer counterLabel and disclaimer text
   */
  updateDialogFooter = () => this.dispatch('updatePreviewDeliverablesFooter', '', { filesCount: this.getNumberOfSelectedFiles() });

  renderBody = () => {
    const { submissions, activeTabLanguages, activeTab } = this.state;
    let renderedCheckboxesLength = 0, submissionGroup;
    let headerCheckboxLabel = this.translate('languages');
    let secondaryHeader = this.translate('batches');
    if (!activeTabLanguages) {
       headerCheckboxLabel = this.translate('batches');
       secondaryHeader = this.translate('languages');
    }
    return (
      <>
        <PuzzleToggle
          id={this.moduleName}
          switcherLabel={this.translate('SRL-switcherLabel')}
          activeTab={activeTab}
          setActiveTab={this.setActiveTab}
          checked={!activeTabLanguages}
          switcherOptions={[{
            name: 'language',
            locale: this.translate('SRL-firstOptionLabel')
          }, {
            name: 'batch',
            locale: this.translate('SRL-secondOptionLabel')
          }]}
        />
        <Container className={'pd-empty-sticky-container'} />
        <Container
          ariaLabel={this.translate('SRL-previewDeliverablesTable')}
          className={'tp-preview-deliverables'}
          role="grid"
          tabIndex={0}
        >
          <Container role="rowgroup">
            <Container role="row" className={'pd-header-row'}>
              <Container role="columnheader" className={'pd-header-cell tp-download-all-submissions'}>
                <PuzzleCheckbox
                  className={`${this.areAllChecked() ? 'qa-fakeClass-checked' : ''} k-hbox k-align-items-center`}
                  onChange={this.onCheckDownloadAllHandler}
                  checked={this.areAllChecked()}
                  label={headerCheckboxLabel}
                />
              </Container>
              <Container role="columnheader" className={'pd-header-cell tp-text-semibold'}>
                {secondaryHeader}
              </Container>
            </Container>
          </Container>
          <>
            {!_.isEmpty(submissions) &&
              submissions.map((submission: SUBMISSION) => {
                submissionGroup = this.renderSubmission(submission, renderedCheckboxesLength);
                renderedCheckboxesLength += submission.jobs.length;
                return submissionGroup
              })}
          </>
        </Container>
      </>
    )

  }
}

export default PreviewDeliverables;
